<template>
    <div v-if="featureAllowed || mode === 'none'">
        <!-- Feature allowed OR mode="none" (never lock, e.g. Identity/SSO) - show banner if unavailable -->
        <q-banner v-if="!featureAllowed && mode === 'none'" class="feature-gate-banner q-mb-lg" rounded>
            <template v-slot:avatar>
                <q-icon name="info" color="orange-8" />
            </template>
            <div>
                <strong>{{ label }}</strong> is not included in your current plan.
                You can manage existing settings, but new configurations may require an upgrade.
            </div>
            <template v-slot:action>
                <q-btn flat color="primary" label="View Plans" href="/Settings/Subscription" />
            </template>
        </q-banner>
        <slot />
    </div>
    <div v-else-if="mode === 'read-only'">
        <!-- Upgrade banner + slot content rendered in read-only mode -->
        <q-banner class="feature-gate-banner q-mb-lg" rounded>
            <template v-slot:avatar>
                <q-icon name="lock" color="orange-8" />
            </template>
            <div>
                <strong>{{ label }}</strong> is not available on your current plan.
                Existing data is shown in read-only mode.
            </div>
            <template v-slot:action>
                <q-btn flat color="primary" label="View Plans" href="/Settings/Subscription" />
            </template>
        </q-banner>
        <slot />
    </div>
    <div v-else class="feature-gate-upgrade">
        <div class="feature-gate-upgrade__card">
            <q-icon name="lock" size="48px" class="feature-gate-upgrade__icon" />
            <h3 class="feature-gate-upgrade__title">{{ title }}</h3>
            <p class="feature-gate-upgrade__message">
                This feature is not available on your current plan.
                Upgrade to access {{ featureLabel }}.
            </p>
            <a href="/Settings/Subscription" class="feature-gate-upgrade__btn">
                View Plans &amp; Upgrade
            </a>
        </div>
    </div>
</template>

<script>
/**
 * FeatureGate - wraps page content behind a subscription feature check.
 *
 * Three modes:
 *   mode="lock" (default) - shows upgrade prompt, hides content entirely.
 *   mode="read-only" - shows an upgrade banner + renders content. Child components
 *       can inject `featureReadOnly` to disable write controls.
 *   mode="none" - always renders content (use for pages that must never be locked,
 *       but still want the banner when the feature is unavailable).
 *
 * Usage:
 *   <!-- Full lock (default) -->
 *   <FeatureGate feature="allowBranding" label="Custom Branding">
 *     <BrandingPage />
 *   </FeatureGate>
 *
 *   <!-- Read-only mode with banner -->
 *   <FeatureGate feature="allowSigningGroups" label="Signing Groups" mode="read-only">
 *     <GroupsPage />
 *   </FeatureGate>
 *
 * Child components use inject to check read-only state:
 *   inject: { featureReadOnly: { default: false } }
 *   // Then use: :disabled="featureReadOnly" on buttons, v-if="!featureReadOnly" on actions
 */
export default {
    name: 'FeatureGate',
    props: {
        /** The feature key from _PLAN_FEATURES (e.g., 'allowWebhooks', 'allowBranding') */
        feature: { type: String, required: true },
        /** Human-readable label for the feature shown in the upgrade message */
        label: { type: String, default: 'this feature' },
        /** Override the heading title */
        title: { type: String, default: 'Upgrade Required' },
        /** Gating mode: 'lock' (hide content), 'read-only' (show with banner), 'none' (always show) */
        mode: { type: String, default: 'lock', validator: v => ['lock', 'read-only', 'none'].includes(v) }
    },
    computed: {
        featureAllowed() {
            const features = window._PLAN_FEATURES || {};
            return features[this.feature] === true;
        },
        featureLabel() {
            return this.label;
        },
        isReadOnly() {
            return !this.featureAllowed && this.mode === 'read-only';
        }
    },
    provide() {
        // Reactive provide using a computed-like getter so child components
        // that inject 'featureReadOnly' get a live value.
        const vm = this;
        return {
            featureReadOnly: {
                get isReadOnly() { return vm.isReadOnly; }
            }
        };
    }
};
</script>

<style>
.feature-gate-banner {
    background: #FFF7ED;
    border: 1px solid #FED7AA;
}

.feature-gate-upgrade {
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding: 60px 20px;
}

.feature-gate-upgrade__card {
    text-align: center;
    max-width: 420px;
    padding: 40px 32px;
    border-radius: 12px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
}

.feature-gate-upgrade__icon {
    color: #94a3b8;
    margin-bottom: 16px;
}

.feature-gate-upgrade__title {
    font-size: 20px;
    font-weight: 600;
    color: #0f172a;
    margin: 0 0 8px 0;
}

.feature-gate-upgrade__message {
    font-size: 14px;
    color: #64748b;
    line-height: 1.5;
    margin: 0 0 24px 0;
}

.feature-gate-upgrade__btn {
    display: inline-block;
    padding: 10px 24px;
    background: #4F46E5;
    color: #ffffff;
    text-decoration: none;
    font-size: 14px;
    font-weight: 600;
    border-radius: 8px;
    transition: background 150ms ease;
}

.feature-gate-upgrade__btn:hover {
    background: #4338CA;
    color: #ffffff;
}
</style>
